import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { getTranslation } from '../../../utils/translations';
import advancedBillingService from '../../../services/advancedBillingService';

const DiscountRuleModal = ({ rule, countries, onClose, onSave, language }) => {
  const t = getTranslation(language);
  const [formData, setFormData] = useState({
    ruleName: '',
    ruleType: 'volume',
    description: '',
    discountPercentage: '',
    minOrderQuantity: '',
    earlyPaymentDays: '',
    countryIds: [],
    isActive: true,
    priority: '0'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (rule) {
      setFormData({
        ruleName: rule?.ruleName || '',
        ruleType: rule?.ruleType || 'volume',
        description: rule?.description || '',
        discountPercentage: rule?.discountPercentage?.toString() || '',
        minOrderQuantity: rule?.minOrderQuantity?.toString() || '',
        earlyPaymentDays: rule?.earlyPaymentDays?.toString() || '',
        countryIds: rule?.countryIds || [],
        isActive: rule?.isActive !== undefined ? rule?.isActive : true,
        priority: rule?.priority?.toString() || '0'
      });
    }
  }, [rule]);

  const ruleTypeOptions = [
    { value: 'volume', label: t?.billing?.volumeDiscount || 'Volume Discount' },
    { value: 'early_payment', label: t?.billing?.earlyPayment || 'Early Payment' },
    { value: 'loyalty', label: t?.billing?.loyalty || 'Loyalty' },
    { value: 'seasonal', label: t?.billing?.seasonal || 'Seasonal' },
    { value: 'custom', label: t?.billing?.custom || 'Custom' }
  ];

  const getRuleTypeDescription = (type) => {
    const descriptions = {
      volume: language === 'FR' ?'Remise basée sur la quantité commandée. Nécessite une quantité minimale.' :'Discount based on order quantity. Requires minimum quantity.',
      early_payment: language === 'FR' ?'Remise pour paiement anticipé. Nécessite un nombre de jours d\'avance.' :'Discount for early payment. Requires advance days.',
      loyalty: language === 'FR' ?'Remise pour clients fidèles basée sur l\'historique d\'achat.' :'Discount for loyal customers based on purchase history.',
      seasonal: language === 'FR' ?'Remise saisonnière ou promotionnelle limitée dans le temps.' :'Seasonal or promotional discount limited in time.',
      custom: language === 'FR' ?'Règle de remise personnalisée avec critères spécifiques.' :'Custom discount rule with specific criteria.'
    };
    return descriptions?.[type] || '';
  };

  const countryOptions = [
    ...countries?.map(c => ({ value: c?.id, label: c?.countryName }))
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.ruleName?.trim()) {
      newErrors.ruleName = language === 'FR' ? 'Le nom est requis' : 'Name is required';
    }

    if (formData?.discountPercentage && (parseFloat(formData?.discountPercentage) < 0 || parseFloat(formData?.discountPercentage) > 100)) {
      newErrors.discountPercentage = language === 'FR' ? 'Doit être entre 0 et 100' : 'Must be between 0 and 100';
    }

    if (formData?.ruleType === 'volume' && !formData?.minOrderQuantity) {
      newErrors.minOrderQuantity = language === 'FR' ? 'Quantité minimale requise pour remise volume' : 'Minimum quantity required for volume discount';
    }

    if (formData?.ruleType === 'early_payment' && !formData?.earlyPaymentDays) {
      newErrors.earlyPaymentDays = language === 'FR' ? 'Nombre de jours requis pour paiement anticipé' : 'Days required for early payment discount';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const data = {
        ruleName: formData?.ruleName,
        ruleType: formData?.ruleType,
        description: formData?.description || null,
        discountPercentage: formData?.discountPercentage ? parseFloat(formData?.discountPercentage) : null,
        minOrderQuantity: formData?.minOrderQuantity ? parseInt(formData?.minOrderQuantity) : null,
        earlyPaymentDays: formData?.earlyPaymentDays ? parseInt(formData?.earlyPaymentDays) : null,
        countryIds: formData?.countryIds || [],
        isActive: formData?.isActive,
        priority: parseInt(formData?.priority)
      };

      if (rule) {
        await advancedBillingService?.updateDiscountRule(rule?.id, data);
      } else {
        await advancedBillingService?.createDiscountRule(data);
      }

      onSave();
    } catch (error) {
      console.error('Error saving discount rule:', error);
      alert(language === 'FR' ? 'Erreur lors de l\'enregistrement' : 'Error saving rule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {rule 
                ? (language === 'FR' ? 'Modifier la Règle de Remise' : 'Edit Discount Rule')
                : (language === 'FR' ? 'Créer une Règle de Remise' : 'Create Discount Rule')
              }
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'FR' ?'Configurez les conditions et le montant de la remise automatique' :'Configure conditions and amount for automatic discount'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Section: Informations de base */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Info" size={18} className="text-primary" />
              <h3 className="font-semibold text-foreground">
                {language === 'FR' ? 'Informations de Base' : 'Basic Information'}
              </h3>
            </div>

            <div>
              <Input
                label={language === 'FR' ? 'Nom de la Règle' : 'Rule Name'}
                value={formData?.ruleName}
                onChange={(e) => setFormData({ ...formData, ruleName: e?.target?.value })}
                required
                placeholder={language === 'FR' ? 'Ex: Remise Volume 10%' : 'Ex: Volume Discount 10%'}
              />
              {errors?.ruleName && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <Icon name="AlertCircle" size={12} />
                  {errors?.ruleName}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'FR' ?'Nom descriptif pour identifier facilement cette règle' :'Descriptive name to easily identify this rule'}
              </p>
            </div>

            <div>
              <Select
                label={language === 'FR' ? 'Type de Règle' : 'Rule Type'}
                options={ruleTypeOptions}
                value={formData?.ruleType}
                onChange={(value) => setFormData({ ...formData, ruleType: value })}
                required
              />
              <div className="mt-2 p-3 bg-muted/50 rounded-lg border border-border">
                <p className="text-xs text-foreground flex items-start gap-2">
                  <Icon name="Lightbulb" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                  <span>{getRuleTypeDescription(formData?.ruleType)}</span>
                </p>
              </div>
            </div>

            <div>
              <Input
                label={language === 'FR' ? 'Description (optionnelle)' : 'Description (optional)'}
                value={formData?.description}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                placeholder={language === 'FR' ? 'Détails supplémentaires sur cette règle' : 'Additional details about this rule'}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'FR' ?'Ajoutez des notes ou conditions spécifiques' :'Add notes or specific conditions'}
              </p>
            </div>
          </div>

          {/* Section: Configuration de la Remise */}
          <div className="space-y-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Percent" size={18} className="text-success" />
              <h3 className="font-semibold text-foreground">
                {language === 'FR' ? 'Configuration de la Remise' : 'Discount Configuration'}
              </h3>
            </div>

            <div>
              <Input
                label={language === 'FR' ? 'Pourcentage de Remise' : 'Discount Percentage'}
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData?.discountPercentage}
                onChange={(e) => setFormData({ ...formData, discountPercentage: e?.target?.value })}
                placeholder="0.00"
              />
              {errors?.discountPercentage && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <Icon name="AlertCircle" size={12} />
                  {errors?.discountPercentage}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'FR' ?'Pourcentage de réduction appliqué (0-100%)' :'Percentage reduction applied (0-100%)'}
              </p>
            </div>

            {formData?.ruleType === 'volume' && (
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-2 mb-3">
                  <Icon name="Package" size={16} className="text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      {language === 'FR' ? 'Remise Volume' : 'Volume Discount'}
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                      {language === 'FR' ?'Cette remise s\'applique lorsque la quantité commandée atteint le minimum' :'This discount applies when order quantity reaches the minimum'}
                    </p>
                  </div>
                </div>
                <Input
                  label={language === 'FR' ? 'Quantité Minimale de Commande' : 'Minimum Order Quantity'}
                  type="number"
                  min="1"
                  value={formData?.minOrderQuantity}
                  onChange={(e) => setFormData({ ...formData, minOrderQuantity: e?.target?.value })}
                  placeholder="10"
                  required={formData?.ruleType === 'volume'}
                />
                {errors?.minOrderQuantity && (
                  <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} />
                    {errors?.minOrderQuantity}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'FR' ?'Nombre minimum d\'unités pour bénéficier de la remise' :'Minimum number of units to qualify for discount'}
                </p>
              </div>
            )}

            {formData?.ruleType === 'early_payment' && (
              <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-start gap-2 mb-3">
                  <Icon name="Clock" size={16} className="text-green-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900 dark:text-green-100">
                      {language === 'FR' ? 'Remise Paiement Anticipé' : 'Early Payment Discount'}
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">
                      {language === 'FR' ?'Cette remise récompense les paiements effectués avant l\'échéance' :'This discount rewards payments made before due date'}
                    </p>
                  </div>
                </div>
                <Input
                  label={language === 'FR' ? 'Jours d\'Avance Requis' : 'Required Advance Days'}
                  type="number"
                  min="1"
                  value={formData?.earlyPaymentDays}
                  onChange={(e) => setFormData({ ...formData, earlyPaymentDays: e?.target?.value })}
                  placeholder="15"
                  required={formData?.ruleType === 'early_payment'}
                />
                {errors?.earlyPaymentDays && (
                  <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} />
                    {errors?.earlyPaymentDays}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'FR' ?'Nombre de jours avant l\'échéance pour bénéficier de la remise' :'Number of days before due date to qualify for discount'}
                </p>
              </div>
            )}
          </div>

          {/* Section: Portée et Priorité */}
          <div className="space-y-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Settings" size={18} className="text-primary" />
              <h3 className="font-semibold text-foreground">
                {language === 'FR' ? 'Portée et Priorité' : 'Scope and Priority'}
              </h3>
            </div>

            <div>
              <Select
                label={language === 'FR' ? 'Pays Applicables' : 'Applicable Countries'}
                options={countryOptions}
                value={formData?.countryIds}
                onChange={(value) => setFormData({ ...formData, countryIds: value })}
                multiple
                searchable={true}
                clearable={true}
                placeholder={language === 'FR' ? 'Sélectionnez un ou plusieurs pays' : 'Select one or more countries'}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'FR' ?'Sélectionnez les pays où cette règle s\'applique. Laissez vide pour tous les pays.' :'Select countries where this rule applies. Leave empty for all countries.'}
              </p>
            </div>

            <div>
              <Input
                label={language === 'FR' ? 'Priorité' : 'Priority'}
                type="number"
                min="0"
                value={formData?.priority}
                onChange={(e) => setFormData({ ...formData, priority: e?.target?.value })}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'FR' ?'Ordre d\'application (0 = priorité la plus élevée). Utile si plusieurs règles s\'appliquent.'
                  : 'Application order (0 = highest priority). Useful when multiple rules apply.'}
              </p>
            </div>

            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, isActive: !formData?.isActive })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData?.isActive ? 'bg-success' : 'bg-muted-foreground'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData?.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">
                  {language === 'FR' ? 'Règle Active' : 'Active Rule'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {language === 'FR' ?'Activez pour appliquer automatiquement cette règle aux commandes' :'Enable to automatically apply this rule to orders'}
                </p>
              </div>
              <Icon 
                name={formData?.isActive ? 'CheckCircle' : 'Circle'} 
                size={20} 
                className={formData?.isActive ? 'text-success' : 'text-muted-foreground'}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button type="submit" fullWidth loading={loading} iconName="Save" iconPosition="left">
              {language === 'FR' ? 'Enregistrer la Règle' : 'Save Rule'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={onClose} iconName="X" iconPosition="left">
              {t?.cancel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DiscountRuleModal;